import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CareShell } from "@/components/CareShell";
import { toast } from "sonner";
import { Users, Copy, Share2, Trash2, ShieldCheck, UserPlus, Loader2, HeartHandshake, ArrowLeft } from "lucide-react";

const CORAL = "#F43F5E";

export default function CareFamily() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [code, setCode] = useState("");

    const load = () => api.get("/care/guardian/links").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    useEffect(() => { load(); }, []);

    const genInvite = async () => {
        setBusy(true);
        try { const { data: d } = await api.post("/care/guardian/invite"); setData((p) => ({ ...p, invite: d })); toast.success("Invite code ready"); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    const copyCode = (c) => { navigator.clipboard?.writeText(c); toast.success("Code copied"); };
    const shareCode = (c) => {
        const text = `Join HOMIIE Care as my guardian. Open HOMIIE → Create account → "I'm a family guardian" and enter code: ${c}`;
        if (navigator.share) navigator.share({ title: "HOMIIE Care guardian invite", text }).catch(() => {});
        else { navigator.clipboard?.writeText(text); toast.success("Invite copied"); }
    };
    const removeGuardian = async (guardian_uid) => {
        setBusy(true);
        try { await api.post("/care/guardian/unlink", { guardian_uid }); toast.success("Guardian removed"); load(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    const linkParent = async () => {
        if (!code.trim()) return toast.error("Enter the invite code");
        setBusy(true);
        try { await api.post("/care/guardian/link", { code: code.trim().toUpperCase() }); toast.success("Linked! You now manage their Care."); await refresh(); load(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    const unlinkSelf = async () => {
        setBusy(true);
        try { await api.post("/care/guardian/unlink", {}); toast.success("Unlinked"); await refresh(); load(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };

    const isGuardian = data?.role === "guardian" || !!user?.guardian_of;

    return (
        <CareShell>
            <div className="px-5 pt-4">
                <button onClick={() => nav("/profile")} className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-navy/50" data-testid="family-back-btn"><ArrowLeft size={16} /> Back</button>
                <div className="flex items-center gap-2.5">
                    <div className="rounded-2xl p-2.5" style={{ backgroundColor: `${CORAL}1a`, color: CORAL }}><Users size={22} /></div>
                    <div>
                        <h1 className="font-heading text-xl font-extrabold text-navy">Family & Guardians</h1>
                        <p className="text-xs text-navy/50">Let a family member help manage Care</p>
                    </div>
                </div>
            </div>

            {!data && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F43F5E]" /></div>}

            {data && isGuardian && (
                <div className="space-y-4 px-5 py-5" data-testid="guardian-view">
                    {data.parent ? (
                        <div className="rounded-2xl border border-[#F43F5E]/20 bg-white p-5">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full text-lg font-extrabold text-white" style={{ backgroundColor: CORAL }}>{data.parent.name?.[0] || "P"}</div>
                                <div className="flex-1">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-navy/40">You manage Care for</p>
                                    <h3 className="font-heading text-lg font-extrabold text-navy" data-testid="guardian-parent-name">{data.parent.name}</h3>
                                    <p className="text-xs text-navy/50">{data.parent.customer_id}</p>
                                </div>
                                <ShieldCheck size={22} className="text-emerald-500" />
                            </div>
                            <p className="mt-4 rounded-xl bg-[#FFF3E8] p-3 text-xs leading-relaxed text-navy/70">
                                You can book Care visits, buy or renew plans, manage health records and receive emergency alerts on their behalf.
                            </p>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <button onClick={() => nav("/care")} className="rounded-xl bg-[#F43F5E] py-3 text-sm font-bold text-white transition-transform active:scale-95" data-testid="guardian-open-care-btn">Open Care</button>
                                <button onClick={unlinkSelf} disabled={busy} className="rounded-xl border border-red-200 py-3 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60" data-testid="guardian-unlink-btn">Unlink</button>
                            </div>
                        </div>
                    ) : (
                        <LinkForm code={code} setCode={setCode} onLink={linkParent} busy={busy} />
                    )}
                </div>
            )}

            {data && !isGuardian && (
                <div className="space-y-4 px-5 py-5" data-testid="parent-view">
                    <div className="rounded-2xl border border-[#F43F5E]/20 bg-white p-5">
                        <div className="flex items-center gap-2"><HeartHandshake size={18} className="text-[#F43F5E]" /><h3 className="font-heading font-bold text-navy">Invite a guardian</h3></div>
                        <p className="mt-1 text-xs text-navy/55">Share this code with your son, daughter or carer. They create a HOMIIE account and enter it to help manage your Care.</p>
                        {data.invite?.code ? (
                            <div className="mt-4">
                                <div className="flex items-center justify-between rounded-xl border border-dashed border-[#F43F5E]/40 bg-[#FFF3E8] px-4 py-3">
                                    <span className="font-heading text-2xl font-extrabold tracking-[0.3em] text-navy" data-testid="invite-code">{data.invite.code}</span>
                                    <button onClick={() => copyCode(data.invite.code)} className="rounded-lg p-2 text-[#F43F5E] hover:bg-white" data-testid="copy-invite-btn"><Copy size={18} /></button>
                                </div>
                                <button onClick={() => shareCode(data.invite.code)} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#F43F5E] py-3 text-sm font-bold text-white transition-transform active:scale-95" data-testid="share-invite-btn"><Share2 size={16} /> Share invite</button>
                            </div>
                        ) : (
                            <button onClick={genInvite} disabled={busy} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#F43F5E] py-3 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-60" data-testid="generate-invite-btn">
                                {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Generate invite code
                            </button>
                        )}
                    </div>

                    <div className="rounded-2xl border border-navy/10 bg-white p-5">
                        <h3 className="mb-1 font-heading font-bold text-navy">Linked guardians</h3>
                        {(!data.guardians || data.guardians.length === 0) ? (
                            <p className="py-3 text-sm text-navy/45" data-testid="no-guardians">No guardians linked yet.</p>
                        ) : (
                            <div className="mt-2 space-y-2" data-testid="guardians-list">
                                {data.guardians.map((g) => (
                                    <div key={g.guardian_uid} className="flex items-center gap-3 rounded-xl border border-navy/10 p-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F43F5E]/10 text-sm font-bold text-[#F43F5E]">{g.name?.[0] || "G"}</div>
                                        <div className="flex-1"><p className="text-sm font-semibold text-navy">{g.name}</p><p className="text-xs text-navy/45">{g.phone || g.customer_id}</p></div>
                                        <button onClick={() => removeGuardian(g.guardian_uid)} disabled={busy} className="rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:opacity-60" data-testid={`remove-guardian-${g.guardian_uid}`}><Trash2 size={16} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </CareShell>
    );
}

function LinkForm({ code, setCode, onLink, busy }) {
    return (
        <div className="rounded-2xl border border-[#F43F5E]/20 bg-white p-5" data-testid="guardian-link-form">
            <div className="flex items-center gap-2"><UserPlus size={18} className="text-[#F43F5E]" /><h3 className="font-heading font-bold text-navy">Link to a family member</h3></div>
            <p className="mt-1 text-xs text-navy/55">Enter the invite code your family member generated in their HOMIIE Care → Family & Guardians.</p>
            <input data-testid="link-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. 7FQ2KD" maxLength={6}
                className="mt-4 w-full rounded-xl border border-navy/15 px-4 py-3 text-center font-heading text-xl font-extrabold tracking-[0.3em] text-navy outline-none focus:border-[#F43F5E]" />
            <button onClick={onLink} disabled={busy} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#F43F5E] py-3 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-60" data-testid="link-submit-btn">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Link account
            </button>
        </div>
    );
}
